import React, { useState, useEffect, useRef } from 'react';
import { 
  Download, 
  FileText, 
  Image, 
  Upload, 
  X, 
  Edit2, 
  Save, 
  Trash2, 
  Plus, 
  AlertTriangle 
} from 'lucide-react';
import { __ } from './translations';

const OtherDocumentsManager = ({ companyId, nonce, restURL }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newDocument, setNewDocument] = useState({
    title: '',
    description: '',
    file: null
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, [companyId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${restURL}board-member/v1/companies/${companyId}/other-documents`, {
        headers: {
          'X-WP-Nonce': nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch documents');
      }

      const data = await response.json();
      setDocuments(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching documents:', err);
      setError(__('Failed to load documents'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewDocument({
      ...newDocument,
      [name]: value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setNewDocument({
      ...newDocument,
      file
    });
  };

  const handleSubmit = async () => {
    if (!newDocument.title || !newDocument.file) {
      setError(__('Title and file are required'));
      return;
    }

    const formData = new FormData();
    formData.append('document_title', newDocument.title);
    formData.append('document_description', newDocument.description);
    formData.append('document_file', newDocument.file);

    try {
      setUploading(true);
      const response = await fetch(`${restURL}board-member/v1/companies/${companyId}/other-documents`, {
        method: 'POST',
        headers: {
          'X-WP-Nonce': nonce
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload document');
      }

      // Reset form
      setNewDocument({
        title: '',
        description: '',
        file: null
      });
      setShowAddForm(false);
      
      // Refetch documents to update the list
      await fetchDocuments();
      
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.message || __('Failed to upload document'));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm(__('Are you sure you want to delete this document?'))) {
      return;
    }

    try {
      const response = await fetch(`${restURL}board-member/v1/companies/${companyId}/other-documents/${docId}`, {
        method: 'DELETE',
        headers: {
          'X-WP-Nonce': nonce
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      // Remove from local state
      setDocuments(documents.filter((doc, index) => index !== docId));
      
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(__('Failed to delete document'));
    }
  };

  const handleEdit = (docId) => {
    setEditingDoc({
      id: docId,
      title: documents[docId].title,
      description: documents[docId].description
    });
  };

  const handleEditSave = async () => {
    try {
      const response = await fetch(`${restURL}board-member/v1/companies/${companyId}/other-documents/${editingDoc.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-WP-Nonce': nonce
        },
        body: JSON.stringify({
          document_title: editingDoc.title,
          document_description: editingDoc.description
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update document');
      }

      // Update local state
      const updatedDocs = [...documents];
      updatedDocs[editingDoc.id] = {
        ...updatedDocs[editingDoc.id],
        title: editingDoc.title,
        description: editingDoc.description
      };
      
      setDocuments(updatedDocs);
      setEditingDoc(null);
      
    } catch (err) {
      console.error('Error updating document:', err);
      setError(__('Failed to update document'));
    }
  };

  const handleEditCancel = () => {
    setEditingDoc(null);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditingDoc({
      ...editingDoc,
      [name]: value
    });
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType && fileType.includes('pdf')) {
      return <FileText className="h-5 w-5 text-red-500" />;
    }
    if (fileType && fileType.includes('image')) {
      return <Image className="h-5 w-5 text-blue-500" />;
    }
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  if (loading) {
    return <div className="p-4">{__('Loading documents...')}</div>;
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
      <div className="border-b border-gray-200 bg-gray-50 px-6 py-3">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">
            {__('Other Documents')}
          </h3>
          {documents.length < 10 && !showAddForm && (
            <button
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center"
              onClick={() => setShowAddForm(true)}
            >
              <Plus className="h-4 w-4 mr-1" />
              {__('Add Document')}
            </button>
          )}
        </div>
      </div>
      
      <div className="p-6">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md flex items-start">
            <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
        
        {/* Add Document Form */}
        {showAddForm && (
          <div className="mb-6 p-4 border border-gray-200 rounded-lg">
            <div className="flex justify-between items-center mb-4">
              <h4 className="font-medium">{__('Add New Document')}</h4>
              <button 
                onClick={() => setShowAddForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div>
              <div className="mb-4">
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  {__('Document Title')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={newDocument.title}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="mb-4">
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                  {__('Description')}
                </label>
                <textarea
                  id="description"
                  name="description"
                  value={newDocument.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {__('Document File')} <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg"
                />
                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={triggerFileInput}
                    className="px-3 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 flex items-center"
                  >
                    <Upload className="h-4 w-4 mr-1" />
                    {__('Choose File')}
                  </button>
                  <span className="ml-3 text-sm text-gray-500">
                    {newDocument.file ? newDocument.file.name : __('No file chosen')}
                  </span>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  {__('Allowed file types: PDF, PNG, JPG, JPEG')}
                </p>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowAddForm(false)}
                  className="mr-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  {__('Cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      <span className="animate-spin mr-2">
                        ⟳
                      </span>
                      {__('Uploading...')}
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-1" />
                      {__('Upload')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Documents List */}
        {documents.length > 0 ? (
          <div className="space-y-4">
            {documents.map((doc, index) => (
              <div key={index} className="p-4 border border-gray-200 rounded-lg">
                {editingDoc && editingDoc.id === index ? (
                  <div className="edit-form">
                    <div className="mb-3">
                      <label htmlFor={`edit-title-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        {__('Document Title')}
                      </label>
                      <input
                        type="text"
                        id={`edit-title-${index}`}
                        name="title"
                        value={editingDoc.title}
                        onChange={handleEditChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label htmlFor={`edit-description-${index}`} className="block text-sm font-medium text-gray-700 mb-1">
                        {__('Description')}
                      </label>
                      <textarea
                        id={`edit-description-${index}`}
                        name="description"
                        value={editingDoc.description}
                        onChange={handleEditChange}
                        rows="3"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={handleEditCancel}
                        className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 flex items-center"
                      >
                        <X className="h-4 w-4 mr-1" />
                        {__('Cancel')}
                      </button>
                      
                      <button
                        onClick={handleEditSave}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                      >
                        <Save className="h-4 w-4 mr-1" />
                        {__('Save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start justify-between">
                    <div className="flex items-start flex-1">
                      <div className="flex-shrink-0 pt-1 mr-3">
                        {getFileIcon(doc.file?.type)}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{doc.title}</h4>
                        {doc.description && (
                          <p className="text-sm text-gray-600 mt-1">{doc.description}</p>
                        )}
                        <div className="mt-2">
                          <a
                            href={doc.file?.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            {__('Download')}
                          </a>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(index)}
                        className="p-1 text-gray-500 hover:text-blue-600"
                        title={__('Edit')}
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                      
                      <button
                        onClick={() => handleDelete(index)}
                        className="p-1 text-gray-500 hover:text-red-600"
                        title={__('Delete')}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-4 text-center text-gray-500">
            {__('No documents have been uploaded yet.')}
          </div>
        )}
        
        {documents.length >= 10 && (
          <div className="mt-4 p-3 bg-yellow-50 text-yellow-800 rounded flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {__('Maximum limit of 10 documents reached. Delete some documents to add more.')}
          </div>
        )}
      </div>
    </div>
  );
};

export default OtherDocumentsManager;